import React, { useState, useRef, useEffect, useCallback, useMemo, useLayoutEffect, ChangeEvent } from 'react';
import moment from 'moment-timezone';
import { getTimezoneList, setPageTitle } from '../../utils/functions';
import SingleForm from '../core/input/Form/SingleForm';
import CourseCard from './CourseCard';
import HelpTag from '../core/display/HelpTag';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import Button from '../core/button/Button/Button';
import Calendar, { CalendarRange, getCalendarPatternCssProps } from '../core/display/Calendar/Calendar';
import { openModal, useModalContext } from '../../contexts/ModalContext';
import { AsyncPhaseDeadlinesExistence } from '../../types/types';
import { getAsyncPhaseDeadlinesExistence } from '../../utils/requests';
import _ from 'lodash';
import Toggle from '../core/input/Toggle/Toggle';
import { Contract } from '../../types/types';
const { Row, Col, FormGroup, InputGroup } = SingleForm;

interface Props {
  initCourseId?: string;
  initCourseName?: string;
  initDiscipline?: string;
  initUniversity?: string;
  initTimeZone?: string;
  initLabel?: string;
  initStudentPurchaseEnabled?: 'true' | 'false';
  initAsyncEnabled?: 'true' | 'false';
  initCourseLevelAssignmentPublicationTime?: string;
  initCourseLevelSubmissionDeadline?: string;
  initCourseLevelReviewDeadline?: string;
  initCourseLevelFeedbackDeadline?: string;
  initCourseLevelReflectionDeadline?: string;
  initCourseLevelAssignmentEndDeadline?: string;
  initDeadlineTime?: string;
  onSubmit?: (arg0: FormData, callback: () => void) => void;
  title: string;
  type: 'NEW' | 'EDIT' | 'COPY';
  initContracts?: Contract[];
  initContractId?: string;
}

type Deadline = {
  id:
    | 'courseLevelAssignmentPublicationTime'
    | 'courseLevelSubmissionDeadline'
    | 'courseLevelReviewDeadline'
    | 'courseLevelFeedbackDeadline'
    | 'courseLevelReflectionDeadline'
    | 'courseLevelAssignmentEndDeadline';
  date: string;
  duration: number;
  title: string;
  className: string;
  color: string;
};

function CourseForm({
  initCourseId = '',
  initCourseName = '',
  initDiscipline = '',
  initUniversity = '',
  initTimeZone,
  initLabel = '',
  initStudentPurchaseEnabled = 'false',
  initAsyncEnabled = 'false',
  onSubmit = () => undefined,
  title,
  type,
  initCourseLevelAssignmentPublicationTime = '',
  initCourseLevelSubmissionDeadline = '',
  initCourseLevelReviewDeadline = '',
  initCourseLevelFeedbackDeadline = '',
  initCourseLevelReflectionDeadline = '',
  initCourseLevelAssignmentEndDeadline = '',
  initDeadlineTime = '',
  initContracts,
  initContractId,
}: Props): JSX.Element {
  useEffect(() => setPageTitle(`${type.charAt(0) + type.slice(1).toLowerCase()} Course`), [type]);
  const user = useSelector((state: RootState) => state.user);
  const { modalDispatch } = useModalContext();

  const date = new Date();
  const month = date.getMonth();
  const currYear = date.getUTCFullYear();

  let season: 'Spring' | 'Summer' | 'Fall' | 'Winter' | 'Full Year' = 'Spring';
  if (month >= 11 && month <= 1) season = 'Winter';
  else if (month >= 2 && month <= 4) season = 'Spring';
  else if (month >= 5 && month <= 7) season = 'Summer';
  else if (month >= 8 && month <= 10) season = 'Fall';

  const years: number[] = [];
  for (let i = currYear; i <= currYear + 3; i++) years.push(i);

  const [name, setName] = useState<string>(initCourseName);
  const [discipline, setDiscipline] = useState<string>(initDiscipline);
  const [institution, setInstitution] = useState<string>(initUniversity);
  const [semester, setSemester] = useState<'Spring' | 'Summer' | 'Fall' | 'Winter' | 'Full Year'>(season);
  const [year, setYear] = useState<string>(currYear + '');
  const [label, setLabel] = useState<string>(initLabel);
  const [studentPurchaseEnabled, setStudentPurchaseEnabled] = useState<'true' | 'false'>(initStudentPurchaseEnabled);
  const [asyncEnabled, setAsyncEnabled] = useState<'true' | 'false'>(initAsyncEnabled);
  const [asyncPhaseDeadlinesExistence, setAsyncPhaseDeadlinesExistence] = useState<
    AsyncPhaseDeadlinesExistence | undefined
  >(undefined);
  const [courseLevelAssignmentPublicationTime, setCourseLevelAssignmentPublicationTime] = useState<string>(
    initCourseLevelAssignmentPublicationTime,
  );
  const [courseLevelSubmissionDeadline, setCourseLevelSubmissionDeadline] = useState<string>(
    initCourseLevelSubmissionDeadline,
  );
  const [courseLevelReviewDeadline, setCourseLevelReviewDeadline] = useState<string>(initCourseLevelReviewDeadline);
  const [courseLevelFeedbackDeadline, setCourseLevelFeedbackDeadline] = useState<string>(
    initCourseLevelFeedbackDeadline,
  );
  const [courseLevelReflectionDeadline, setCourseLevelReflectionDeadline] = useState<string>(
    initCourseLevelReflectionDeadline,
  );
  const [courseLevelAssignmentEndDeadline, setCourseLevelAssignmentEndDeadline] = useState<string>(
    initCourseLevelAssignmentEndDeadline,
  );
  const [deadlineTime, setDeadlineTime] = useState<string>(initDeadlineTime);
  const [courseDeadlinesSetting, setCourseDeadlineSetting] = useState(false);
  const [courseDeadlinesOn, setCourseDeadlinesOn] = useState(false);
  const [deadlines, setDeadlines] = useState<Deadline[]>([]);
  const [contractId, setContractId] = useState<string | undefined>(initContractId);
  const timezone = useRef<string>(initTimeZone ? initTimeZone : moment.tz.guess());
  const timezoneList = useRef<string[]>(getTimezoneList());
  const [selectingIndex, setSelectingIndex] = useState<number | undefined>(undefined);
  const currMoment = moment();

  const toggleAdvancedSettings = () => {
    setCourseDeadlineSetting((prev) => !prev);
  };

  const toggleCourseDeadlines = () => {
    setCourseDeadlinesOn((prev) => !prev);
  };

  useEffect(() => {
    if (!courseDeadlinesSetting) {
      setCourseDeadlinesOn(false);
    }
  }, [courseDeadlinesSetting]);

  useEffect(() => {
    if (initCourseId) {
      getAsyncPhaseDeadlinesExistence(initCourseId, setAsyncPhaseDeadlinesExistence);
    }
  }, [initCourseId]);

  useEffect(() => {
    if (type === 'COPY') {
      setCourseLevelAssignmentPublicationTime(currMoment.add(1, 'd').format('YYYY-MM-DD'));
      setDeadlineTime('23:59');
      if (
        courseLevelSubmissionDeadline !== null ||
        courseLevelReviewDeadline !== null ||
        courseLevelFeedbackDeadline !== null ||
        courseLevelReflectionDeadline !== null ||
        courseLevelAssignmentEndDeadline != null
      ) {
        if (asyncPhaseDeadlinesExistence?.submissionPhase)
          setCourseLevelSubmissionDeadline(currMoment.add(7, 'd').format('YYYY-MM-DD'));
        if (asyncPhaseDeadlinesExistence?.reviewPhase)
          setCourseLevelReviewDeadline(currMoment.add(5, 'd').format('YYYY-MM-DD'));
        if (asyncPhaseDeadlinesExistence?.feedbackPhase)
          setCourseLevelFeedbackDeadline(currMoment.add(2, 'd').format('YYYY-MM-DD'));
        if (asyncPhaseDeadlinesExistence?.reflectionPhase)
          setCourseLevelReflectionDeadline(currMoment.add(5, 'd').format('YYYY-MM-DD'));
        if (asyncPhaseDeadlinesExistence?.asyncDeadline)
          setCourseLevelAssignmentEndDeadline(currMoment.add(5, 'd').format('YYYY-MM-DD'));
      }
    }
  }, [
    asyncPhaseDeadlinesExistence?.asyncDeadline,
    asyncPhaseDeadlinesExistence?.submissionPhase,
    asyncPhaseDeadlinesExistence?.feedbackPhase,
    asyncPhaseDeadlinesExistence?.reflectionPhase,
    asyncPhaseDeadlinesExistence?.reviewPhase,
    courseLevelAssignmentEndDeadline,
    courseLevelFeedbackDeadline,
    courseLevelReflectionDeadline,
    courseLevelReviewDeadline,
    courseLevelSubmissionDeadline,
    currMoment,
    type,
  ]);

  useEffect(() => {
    const initDeadlines: Deadline[] = [];
    initDeadlines.push({
      id: 'courseLevelAssignmentPublicationTime',
      date: courseLevelAssignmentPublicationTime,
      duration: 0,
      title: 'Assignment Publication Date',
      className: 'assignment-publication-date',
      color: '#B8DCFF',
    });
    if (asyncPhaseDeadlinesExistence) {
      if (asyncPhaseDeadlinesExistence.submissionPhase) {
        initDeadlines.push({
          id: 'courseLevelSubmissionDeadline',
          date: courseLevelSubmissionDeadline,
          duration: 0,
          title: 'Assignment Submission Phase',
          className: 'assignment-submission-phase',
          color: '#7878F1',
        });
      }
      if (asyncPhaseDeadlinesExistence.reviewPhase) {
        initDeadlines.push({
          id: 'courseLevelReviewDeadline',
          date: courseLevelReviewDeadline,
          duration: 0,
          title: 'Assignment Review Phase',
          className: 'assignment-review-phase',
          color: '#E676E3',
        });
      }
      if (asyncPhaseDeadlinesExistence.feedbackPhase) {
        initDeadlines.push({
          id: 'courseLevelFeedbackDeadline',
          date: courseLevelFeedbackDeadline,
          duration: 0,
          title: 'Assignment Feedback Phase',
          className: 'assignment-feedback-phase',
          color: '#E4C445',
        });
      }
      if (asyncPhaseDeadlinesExistence.reflectionPhase) {
        initDeadlines.push({
          id: 'courseLevelReflectionDeadline',
          date: courseLevelReflectionDeadline,
          duration: 0,
          title: 'Assignment Reflection Phase',
          className: 'assignment-reflection-phase',
          color: '#3CB371',
        });
      }
      if (asyncPhaseDeadlinesExistence.asyncDeadline) {
        initDeadlines.push({
          id: 'courseLevelAssignmentEndDeadline',
          date: courseLevelAssignmentEndDeadline,
          duration: 0,
          title: 'Async Assignment',
          className: 'async-assignment',
          color: '#7878F1',
        });
      }
    }
    for (let i = 1; i < initDeadlines.length; i++) {
      const lastMoment = moment(initDeadlines[i - 1].date);
      const currMoment = moment(initDeadlines[i].date);
      initDeadlines[i].duration = currMoment.diff(lastMoment, 'days');
    }
    setDeadlines(initDeadlines);
  }, [
    asyncPhaseDeadlinesExistence,
    courseLevelAssignmentEndDeadline,
    courseLevelAssignmentPublicationTime,
    courseLevelFeedbackDeadline,
    courseLevelReflectionDeadline,
    courseLevelReviewDeadline,
    courseLevelSubmissionDeadline,
  ]);

  const handleDurationChange = useCallback((e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    setDeadlines((prevDeadlines) => {
      const newDeadlines = _.clone(prevDeadlines);

      const diff = parseInt(e.target.value) - prevDeadlines[index].duration;
      newDeadlines[index].duration += diff;
      for (let j = index; j < newDeadlines.length; j++) {
        newDeadlines[j].date = moment(newDeadlines[j].date).add(diff, 'days').format('YYYY-MM-DD');
      }
      return newDeadlines;
    });
  }, []);

  const onInvalid = useCallback(
    (heading: string, label: string) =>
      modalDispatch(
        openModal({
          heading,
          label,
          cancelHide: true,
        }),
      ),
    [modalDispatch],
  );

  const validateNewDate = (index: number, date: string): boolean => {
    const currMoment = moment();
    if (currMoment.isAfter(date)) {
      onInvalid('Invalid Date', 'Dates must be in the future');
      return false;
    }

    const selectedMoment = moment(date, 'YYYY-MM-DD');
    if (selectedMoment.diff(currMoment, 'days') < index) {
      onInvalid(
        'Date is Too Soon',
        `The date you've chosen is too soon to give the assignment sufficient time. Please select a date further in the future.`,
      );
      return false;
    }

    return true;
  };

  const handleDateSelect = useCallback((index: number, date: string) => {
    setDeadlines((prevDeadlines) => {
      const newDeadlines = _.clone(prevDeadlines);

      const lowerBound = index - 1 >= 0 ? newDeadlines[index - 1].date : '1000-01-01';
      const upperBound = index + 1 < newDeadlines.length ? newDeadlines[index + 1].date : '9999-12-31';
      if (moment(date).isBetween(lowerBound, upperBound)) {
        newDeadlines[index].date = date;
        if (index > 0) {
          newDeadlines[index].duration = moment(date).diff(lowerBound, 'days');
        }
        if (index + 1 < newDeadlines.length) {
          newDeadlines[index + 1].duration = moment(newDeadlines[index + 1].date).diff(date, 'days');
        }
      } else if (moment(date).isSameOrBefore(lowerBound)) {
        let nextDate = date;
        for (let i = index; i >= 0; i--) {
          newDeadlines[i].date = nextDate;
          if (i - 1 >= 0)
            nextDate = moment(newDeadlines[i].date).subtract(newDeadlines[i].duration, 'days').format('YYYY-MM-DD');
        }
      } else if (moment(date).isSameOrAfter(upperBound)) {
        let nextDate = date;
        for (let i = index; i < newDeadlines.length; i++) {
          newDeadlines[i].date = nextDate;
          if (i + 1 < newDeadlines.length)
            nextDate = moment(newDeadlines[i].date)
              .add(newDeadlines[i + 1].duration, 'days')
              .format('YYYY-MM-DD');
        }
      }
      return newDeadlines;
    });
  }, []);

  const ranges: CalendarRange[] = useMemo(
    () =>
      deadlines.length === 1
        ? [{ color: deadlines[0].color, low: deadlines[0].date, high: deadlines[0].date, label: deadlines[0].title }]
        : deadlines.slice(1).map((deadline, i) => ({
            color: deadline.color,
            low: deadlines[i].date,
            high: deadline.date,
            label: deadline.title,
          })),
    [deadlines],
  );

  const publicationMoment = deadlines[0]?.date
    ? moment(deadlines[0].date, 'YYYY-MM-DD')
    : currMoment.clone().add(1, 'd');

  useEffect(() => {
    if (deadlines[0]?.date !== publicationMoment.format('YYYY-MM-DD')) {
      setDeadlines((prevDeadlines) => {
        const updatedDeadlines = [...prevDeadlines];
        updatedDeadlines[0].date = publicationMoment.format('YYYY-MM-DD');
        return updatedDeadlines;
      });
    }
  }, [publicationMoment, deadlines]);

  const getDeadlineById = (deadlines: Deadline[], id: Deadline['id']): string | undefined => {
    const deadline = deadlines.find((d) => d.id === id);
    return deadline?.date;
  };

  useLayoutEffect(() => {
    const institutionDiv = document.getElementById('contract-filter');
    if (institutionDiv && initContracts) {
      institutionDiv.innerHTML = '';

      initContracts.forEach((item) => {
        const option = document.createElement('option');
        option.text = item.name;
        option.value = item.contractId;
        if (item.contractId === initContractId) {
          option.selected = true;
        }
        institutionDiv.appendChild(option);
      });
    }
  }, [initContracts, initContractId]);

  const handleContractChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setContractId(event.target.value);
  };

  return (
    <SingleForm
      className="course-form"
      title={title}
      submitText={type === 'EDIT' ? 'Save Changes' : 'Create'}
      onSubmit={onSubmit}
      backButton
      backUrl="/course/dashboard"
    >
      <Row>
        <Col>
          <FormGroup>
            <InputGroup iconCode="class" flexDirection="column">
              <label className="sr-only" htmlFor="course-name">
                Course Name
              </label>
              <input
                name="courseName"
                id="course-name"
                type="text"
                placeholder="Course Name"
                required={true}
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                }}
              />
              <label className="sr-only" htmlFor="course-discipline">
                Discipline
              </label>
              <input
                name="discipline"
                id="course-discipline"
                type="text"
                placeholder="Discipline"
                required={true}
                value={discipline}
                onChange={(e) => {
                  setDiscipline(e.target.value);
                }}
              />
            </InputGroup>
          </FormGroup>
          <FormGroup>
            <InputGroup iconCode="place" flexDirection="column">
              <label className="sr-only" htmlFor="course-institution">
                Institution
              </label>
              <input
                name="university"
                id="course-institution"
                type="text"
                placeholder="Institution"
                required={true}
                value={institution}
                onChange={(e) => {
                  setInstitution(e.target.value);
                }}
              />
            </InputGroup>
            {initContracts && initContracts?.length > 0 ? (
              <InputGroup iconCode="folder" flexDirection="column">
                <div className="contract">
                  <select
                    name="contractId"
                    value={contractId}
                    onChange={handleContractChange}
                    id="contract-filter"
                  ></select>
                </div>
              </InputGroup>
            ) : null}

            <InputGroup iconCode="event">
              <label className="sr-only" htmlFor="course-semester">
                Semester
              </label>
              <select
                name="semester"
                id="course-semester"
                required={true}
                value={semester}
                onChange={(e) => {
                  setSemester(e.target.value as 'Spring' | 'Summer' | 'Fall' | 'Winter' | 'Full Year');
                }}
              >
                <option>Spring</option>
                <option>Summer</option>
                <option>Fall</option>
                <option>Winter</option>
                <option>Full Year</option>
              </select>
              <label className="sr-only" htmlFor="course-year">
                Year
              </label>
              <select
                name="year"
                id="course-year"
                required={true}
                value={year}
                onChange={(e) => {
                  setYear(e.target.value);
                }}
              >
                {years.map((yr) => (
                  <option key={yr}>{yr}</option>
                ))}
              </select>
            </InputGroup>
            <InputGroup iconCode="language">
              <label className="sr-only" htmlFor="course-timezone">
                Timezone
              </label>
              <select name="timeZone" id="course-timezone" defaultValue={timezone.current} required={true}>
                {timezoneList.current.map((tz) => (
                  <option key={tz}>{tz}</option>
                ))}
              </select>
            </InputGroup>
          </FormGroup>
          <FormGroup>
            <InputGroup iconCode="label">
              <label className="sr-only" htmlFor="course-label">
                Label (Optional)
              </label>
              <input
                name="label"
                id="course-label"
                type="text"
                placeholder="Label (Optional)"
                value={label}
                onChange={(e) => {
                  setLabel(e.target.value);
                }}
              />
              <HelpTag>
                <b>Labels</b> can be used to identify the course. <i>(i.e. section, schedule...)</i>
              </HelpTag>
            </InputGroup>
          </FormGroup>
          {user.purchasingEnabled ? (
            <FormGroup>
              <InputGroup iconCode="attach_money">
                <label className="sr-only" htmlFor="course-purchase">
                  Purchase Options
                </label>
                <select
                  name="studentPurchaseEnabled"
                  id="course-purchase"
                  required={true}
                  value={studentPurchaseEnabled}
                  onChange={(e) => {
                    setStudentPurchaseEnabled(e.target.value as 'true' | 'false');
                  }}
                  disabled={type === 'EDIT'}
                >
                  <option value="false">Enrollment Seats Purchased By Instructor</option>
                  <option value="true">Enrollment Seats Purchased By Students</option>
                </select>
              </InputGroup>
            </FormGroup>
          ) : null}

          <FormGroup>
            <InputGroup iconCode="schedule">
              <label className="sr-only" htmlFor="course-sync">
                Synchronization Options
              </label>
              <select
                name="asyncEnabled"
                id="course-sync"
                required={true}
                value={asyncEnabled}
                onChange={(e) => {
                  setAsyncEnabled(e.target.value as 'true' | 'false');
                }}
                disabled={type === 'EDIT'}
              >
                <option value="false">Sync Mode</option>
                <option value="true">Async Mode</option>
              </select>
              <HelpTag>
                <p>
                  <b>Sync Mode:</b> Multiple set deadlines and grading
                </p>
                <p>
                  <b>Async Mode:</b> Final deadline only and competency-based grading
                </p>
              </HelpTag>
            </InputGroup>
          </FormGroup>
          {asyncEnabled === 'true' && (type === 'EDIT' || type === 'COPY') ? (
            <FormGroup>
              <InputGroup iconCode="chevron_right">
                <div>
                  <button
                    type="button"
                    className="button-link low"
                    onClick={toggleAdvancedSettings}
                    aria-expanded={courseDeadlinesSetting}
                  >
                    {courseDeadlinesSetting ? 'Hide Advanced Deadline Settings' : 'Show Advanced Deadline Settings'}
                  </button>
                </div>
              </InputGroup>
            </FormGroup>
          ) : null}
          {courseDeadlinesSetting ? (
            <div>
              <Toggle checked={courseDeadlinesOn} onChange={toggleCourseDeadlines}>
                Course Deadlines
              </Toggle>
            </div>
          ) : null}
          {courseDeadlinesOn && (
            <div className="advanced-deadline-content">
              <div>
                <div id="instructions">
                  <p>
                    Choose course level dates and deadlines. These deadlines will be appied to each assignment in the
                    course. To edit a date, click its button below and choose a new date via the Calendar display. Click
                    the help icon for more information:{' '}
                    <HelpTag type="modal" heading="Deadline Selection">
                      <p>
                        <b>Instructions:</b>
                        <br />
                        Each date is clickable. Once clicked, you will be able to select a new date via the Calendar
                        display. You also have the option to control a phase&apos;s length via the Duration selector.
                        Doing so will affect future deadlines accordingly.
                        <br />
                        The time period of a phase is represented visually on the Calendar by the phase&apos;s
                        corresponding color/pattern. Deadlines appear at the end of these phase representations, and
                        appear visually split when they line up with the start of a new phase.
                      </p>
                      <p>
                        <b>Requirements:</b>
                        <br />
                        Dates and deadlines must sequentially remain in the order that they are listed below. For
                        example, the Submission Phase deadline must be after the Publication Date and before the Review
                        Phase deadline. Any selections made that break this order will cause the surrounding dates to be
                        automatically rebalanced to fit the required order of sequence.
                      </p>
                    </HelpTag>
                  </p>
                </div>
                <div className="date-list">
                  <p id="timezone-prompt">
                    All dates in{' '}
                    <b>
                      <i>{timezone.current}</i>
                    </b>{' '}
                    time
                  </p>
                  <div>
                    <h3 id="publicationDateLabel" style={{ display: 'inline-block' }}>
                      Publication Date:
                    </h3>
                    <Button
                      variant="alt rad low"
                      type="button"
                      onClick={() => setSelectingIndex(0)}
                      ariaLabel={`Publication date ${publicationMoment.format(
                        'MMMM DD YYYY',
                      )}; To change, select button and choose a date on the calendar`}
                    >
                      {publicationMoment.format('MM/DD/YYYY')}
                    </Button>
                    <input
                      type="hidden"
                      name="courseLevelAssignmentPublicationTime"
                      value={moment
                        .tz(getDeadlineById(deadlines, 'courseLevelAssignmentPublicationTime') || '', timezone.current)
                        .toISOString()}
                    />
                  </div>
                  <div>
                    <label htmlFor="deadlineTime">Deadline Time:</label>
                    <input
                      type="time"
                      id="deadlineTime"
                      name="deadlineTime"
                      step={60}
                      value={deadlineTime}
                      onChange={(e) => {
                        setDeadlineTime(e.target.value);
                      }}
                      required
                    />
                    <b> {moment().tz(timezone.current).format('z')}</b>
                  </div>

                  {deadlines.slice(1).map((deadline, i) => {
                    const realIndex = i + 1;
                    const deadlineMoment = moment(deadline.date);
                    return (
                      <div key={deadline.id} className={`phase-time-settings ${deadline.className}`}>
                        <h3>
                          <div className="key-color-indicator" style={getCalendarPatternCssProps(i, deadline.color)} />
                          {deadline.title}
                        </h3>
                        <div className="phase-inputs-wrapper">
                          <div>
                            <div>
                              Start:
                              <b>{moment(deadlines[realIndex - 1].date).format('MM/DD/YYYY')}</b>
                            </div>
                            <div>
                              End:
                              <Button
                                variant="alt rad low"
                                type="button"
                                onClick={() => setSelectingIndex(realIndex)}
                                ariaLabel={`${deadline.title} end deadline ${deadlineMoment.format(
                                  'MMMM DD YYYY',
                                )}; To change, select button and choose a date on the calendar`}
                              >
                                {deadlineMoment.format('MM/DD/YYYY')}
                              </Button>
                            </div>
                          </div>
                          <div>
                            <label>
                              Duration:
                              <input
                                id={`${deadline.id}-duration`}
                                type="number"
                                value={deadline.duration}
                                onChange={(e) => handleDurationChange(e, realIndex)}
                                min={1}
                              />
                              days
                            </label>
                          </div>
                        </div>
                        <input
                          type="hidden"
                          name={deadline.id}
                          value={moment.tz(deadline.date, timezone.current).toISOString()}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </Col>
        <Col className="justify-center align-vertical" alignRight>
          <figure className="course-card-preview">
            <CourseCard
              name={name === '' ? 'Course Name' : name}
              discipline={discipline === '' ? 'Discipline' : discipline}
              semester={semester}
              year={parseInt(year)}
              label={label === '' ? 'Label' : label}
              readOnly={true}
            />
            <figcaption>Preview</figcaption>
          </figure>
          {asyncEnabled === 'true' && courseDeadlinesOn ? (
            <Calendar
              className={selectingIndex !== undefined ? 'focus-pulse' : undefined}
              focusTrap={selectingIndex !== undefined}
              onSelect={(date) => {
                if (selectingIndex !== undefined) {
                  if (validateNewDate(selectingIndex, date)) {
                    handleDateSelect(selectingIndex, date);
                  }
                  setSelectingIndex(undefined);
                }
              }}
              onOutsideClick={() => setSelectingIndex(undefined)}
              ranges={ranges}
            />
          ) : null}
        </Col>
      </Row>
    </SingleForm>
  );
}

export default CourseForm;
